import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  XCircleIcon,
  CheckCircle,
  ClipboardCheck,
  Ticket,
  ArrowRight,
  FileText,
  Printer,
} from "lucide-react";
import { NavLink, useParams, useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  DialogContentText,
} from "@mui/material";
import OrderSummary from "./Components/OrderDetailsComponents/OrderSummary";
import OrderItemsTable from "./Components/OrderDetailsComponents/OrderItemsTable";
import { dummyOrders } from "../../../dummyData";
import {
  errorToast,
  successToast,
  infoToast,
} from "../../../services/toast.service";

// Helper function to safely format currency
const formatCurrency = amount => {
  try {
    if (amount === undefined || amount === null) return "₹0";
    return `₹${parseFloat(amount).toLocaleString()}`;
  } catch (error) {
    console.error("Error formatting currency:", error);
    return "₹0";
  }
};

const OrderDetailsScreen = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);

  // Rejection dialog states
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectionReasonError, setRejectionReasonError] = useState("");

  // Find the order in dummy data
  useEffect(() => {
    try {
      const foundOrder =
        dummyOrders.find(order => order._id === orderId) || dummyOrders[0];
      if (!foundOrder) {
        console.error("No order found");
        return;
      }

      // Initialize order with safe default values
      const updatedOrder = {
        ...foundOrder,
        subtotal: 35500, // Default value from screenshot
        discount: 500, // Default value from screenshot
        tax: 6300, // Default value from screenshot
        grandTotal: 41300, // Default value from screenshot
        redeemedVouchers: [
          { code: "FIRST50", description: "₹500 off on your first order" },
        ],
        status: foundOrder.status || "Pending",
      };

      setOrder(updatedOrder);
    } catch (error) {
      console.error("Error setting up order:", error);
    }
  }, [orderId]);

  // Handle order items updated
  const handleOrderItemsUpdated = updatedTotals => {
    if (!order) return;

    try {
      const newOrder = {
        ...order,
        subtotal: updatedTotals?.subtotal || order.subtotal || 0,
        tax: updatedTotals?.tax || order.tax || 0,
        grandTotal: updatedTotals?.grandTotal || order.grandTotal || 0,
      };
      setOrder(newOrder);
      successToast("Order details updated successfully");
    } catch (error) {
      console.error("Error updating order totals:", error);
    }
  };

  // Open rejection dialog
  const handleOpenRejectDialog = () => {
    setRejectDialogOpen(true);
    setRejectionReason("");
    setRejectionReasonError("");
  };

  // Close rejection dialog
  const handleCloseRejectDialog = () => {
    setRejectDialogOpen(false);
  };

  // Handle reject order
  const handleRejectOrder = () => {
    if (!rejectionReason.trim()) {
      setRejectionReasonError("Please provide a reason for rejection");
      return;
    }

    if (order) {
      // In a real app, this would be an API call
      const updatedOrder = {
        ...order,
        status: "Rejected",
        rejectionReason: rejectionReason,
      };
      setOrder(updatedOrder);
      successToast("Order rejected successfully");
      setRejectDialogOpen(false);

      // Navigate back to orders after a delay
      setTimeout(() => {
        navigate("/orders");
      }, 1500);
    }
  };

  // Handle approve order
  const handleApproveAndNext = () => {
    if (order) {
      // In a real app, this would be an API call
      const updatedOrder = {
        ...order,
        status: "Approved",
      };
      setOrder(updatedOrder);

      // Navigate to bill generation page
      navigate(`/orders/${orderId}/generate-bill`);
    }
  };

  // Handle view bill in demo mode
  const handleViewBill = () => {
    navigate(`/orders/${orderId}/view-bill`);
  };

  // If order is not loaded yet, show loading
  if (!order) {
    return <div className="p-8 text-center">Loading order details...</div>;
  }

  return (
    <div className="bg-white h-full">
      {/* Header Section */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-4">
          <NavLink
            to="/orders"
            className="p-1.5 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors">
            <ChevronLeft size={20} className="text-gray-600" />
          </NavLink>
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            Order Details
          </h1>
          <div
            className={`ml-4 px-3 py-1 rounded-full text-sm font-medium
            ${
              order?.status === "Pending"
                ? "bg-yellow-100 text-yellow-800"
                : order?.status === "Approved"
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }
          `}>
            {order?.status || "Pending"}
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 gap-6">
          {/* Order Information Card - Remove Amount from OrderSummary */}
          <OrderSummary order={{ ...order, totalAmount: undefined }} />

          {/* Order Items Table */}
          <OrderItemsTable
            orderId={order._id}
            onOrderItemsUpdated={handleOrderItemsUpdated}
            isApproved={order.status === "Approved"}
          />

          {/* Action Buttons - Bottom positioned for better UX */}
          {order.status === "Pending" && (
            <div className="flex items-center justify-end gap-4 mt-4 pb-8">
              <button
                onClick={handleOpenRejectDialog}
                className="flex items-center gap-2 px-5 py-2.5 text-red-600 border border-red-600 hover:bg-red-50 rounded-lg transition-colors">
                <XCircleIcon size={18} />
                <span className="font-medium">Reject Order</span>
              </button>

              <button
                onClick={handleApproveAndNext}
                className="flex items-center gap-2 px-5 py-2.5 text-white bg-[#5652B0] hover:bg-[#4A47A3] rounded-lg transition-colors">
                <FileText size={18} />
                <span className="font-medium">Generate Bill</span>
                <ArrowRight size={16} />
              </button>
            </div>
          )}

          {order.status === "Approved" && (
            <div className="flex items-center justify-end gap-4 mt-4 pb-8">
              <button
                onClick={handleViewBill}
                className="flex items-center gap-2 px-5 py-2.5 text-white bg-[#5652B0] hover:bg-[#4A47A3]  rounded-lg transition-colors">
                <Printer size={18} />
                <span className="font-medium">View Bill</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Rejection Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={handleCloseRejectDialog}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxWidth: "480px",
            width: "100%",
            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
          },
        }}>
        <DialogTitle
          sx={{
            fontSize: "1.25rem",
            fontWeight: 600,
            color: "#1F2937",
            borderBottom: "1px solid #E5E7EB",
            padding: "20px 24px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}>
          <XCircleIcon size={22} className="text-red-500" />
          Reject Order
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          <p className="text-gray-600 mb-5 text-sm">
            Please provide a reason for rejecting this order. This information
            will help improve our services and will be shared with the customer.
          </p>
          <TextField
            autoFocus
            margin="dense"
            id="rejection-reason"
            label="Rejection Reason"
            type="text"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={rejectionReason}
            onChange={e => {
              setRejectionReason(e.target.value);
              if (e.target.value.trim()) {
                setRejectionReasonError("");
              }
            }}
            error={!!rejectionReasonError}
            helperText={rejectionReasonError}
            placeholder="e.g., Insufficient stock, pricing discrepancies, or customer request to cancel."
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                "&:hover fieldset": {
                  borderColor: "#9CA3AF",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#5652B0",
                },
              },
              "& .MuiFormLabel-root.Mui-focused": {
                color: "#5652B0",
              },
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            padding: "16px 24px 24px",
            justifyContent: "flex-end",
            gap: "12px",
          }}>
          <Button
            onClick={handleCloseRejectDialog}
            sx={{
              color: "rgba(0, 0, 0, 0.6)",
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "8px",
              padding: "8px 16px",
            }}>
            Cancel
          </Button>
          <Button
            onClick={handleRejectOrder}
            variant="contained"
            sx={{
              bgcolor: "#EF4444",
              "&:hover": { bgcolor: "#DC2626" },
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "8px",
              padding: "8px 20px",
              boxShadow: "none",
            }}>
            Reject Order
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OrderDetailsScreen;
